import React, { createContext, useState, useEffect, useContext } from 'react';

/**
 * H4: 网络状态监测
 *
 * 监听浏览器 online/offline 事件，提供全局网络状态。
 *
 * 用法：
 * - 根组件包裹 <NetworkStatusProvider>
 * - 子组件通过 useNetworkStatus() 获取 { isOnline }
 */

// 网络状态上下文
export const NetworkStatusContext = createContext({
    isOnline: true
});

/**
 * 网络状态提供者组件
 * 监听 window online/offline 事件，自动更新状态
 */
export const NetworkStatusProvider = ({ children }) => {
    const [isOnline, setIsOnline] = useState(
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );

    useEffect(() => {
        const handleOnline = () => {
            console.log('[Network] 网络已恢复');
            setIsOnline(true);
        };

        const handleOffline = () => {
            console.log('[Network] 网络已断开');
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return (
        <NetworkStatusContext.Provider value={{ isOnline }}>
            {children}
            {/* 离线提示条 */}
            {!isOnline && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    background: '#ff4757',
                    color: '#fff',
                    textAlign: 'center',
                    padding: '8px 16px',
                    fontSize: '13px',
                    zIndex: 10000,
                    fontWeight: 500
                }}>
                    ⚠️ 网络连接已断开，部分功能可能不可用
                </div>
            )}
        </NetworkStatusContext.Provider>
    );
};

/**
 * 网络状态 Hook
 * @returns {{ isOnline: boolean }} 当前网络是否在线
 */
export const useNetworkStatus = () => {
    const context = useContext(NetworkStatusContext);
    if (!context) {
        // 未包裹 Provider 时降级返回在线状态
        return { isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true };
    }
    return context;
};
